import { Box, Grid, styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  FlexWorkerDataEdit,
  HQGetFlexWorkerDetailsResponse,
  Level,
  WorkerEmploymentType,
} from '@types';
import {
  compareDateStrings,
  dateOfBirthValidator,
  validateDateString,
} from 'shared/utils/validate-date';
import { LwFormDate, LwFormInput, LwFormSelect } from 'redesign';
import { shouldShowBsnField } from 'shared/utils/flexworkers-bsn';
import { PlatformSettings } from 'shared/services';
import {
  getAvailableLevelUpgrades,
  getNextLevelRequirementsText,
} from '../../../../../../shared/utils/level-utils';
import { IconQuestion } from '../../../../../../assets/img';
import { useFeatureFlagContext } from '../../../../../../shared/contexts/feature-flag-provider';
import { getWorkerAgeFromFormattedDate, isWorkerUnderMinumumAge } from '../utils/worker-age';

type FlexWorkerFormEditProps = {
  flexWorkerData: HQGetFlexWorkerDetailsResponse;
  platformSettings: PlatformSettings;
};

export const FlexWorkerFormEdit = ({
  flexWorkerData,
  platformSettings,
}: FlexWorkerFormEditProps) => {
  const { control, watch, getValues, resetField } = useFormContext<FlexWorkerDataEdit>();
  const { allowCandidateOnboarding, allowEmploymentId, allowRequiredVatForWorkActions } =
    useFeatureFlagContext();

  const BSStartDate = watch('smallBusinessSchemeStartDate');
  const BSEndDate = watch('smallBusinessSchemeEndDate');
  const level = getValues('level');

  const validateBSStartEndDateComparison = () =>
    BSStartDate?.length && BSEndDate?.length && !compareDateStrings(BSStartDate, BSEndDate)
      ? 'De opgegeven einddatum is eerder dan de begindatum'
      : undefined;

  const validateBSStartDateExists = () => {
    const BSStartDateExists = BSStartDate && BSStartDate.length > 0;
    const BSEndDateExists = BSEndDate && BSEndDate.length > 0;
    return !BSStartDateExists && BSEndDateExists ? 'Oops, geen KOR begindatum bekend' : undefined;
  };

  const validateBSDate = (value: string | undefined | null) =>
    value && value.length > 0 && !validateDateString(value)
      ? 'De datum die je invult lijkt niet te kloppen'
      : undefined;

  const validateDateOfBirth = (value: any) =>
    value && value.length > 0 && !dateOfBirthValidator(value)
      ? 'De datum die je invult lijkt niet te kloppen'
      : undefined;

  const ageLimit = platformSettings.minimumTempWorkerAge;
  const workerAge = getWorkerAgeFromFormattedDate(watch('dateOfBirth'));
  const isWorkerUnderAge = isWorkerUnderMinumumAge(workerAge, ageLimit);

  const formLevel = getValues('level');
  if (isWorkerUnderAge && formLevel === 'WORKER') {
    resetField('level');
  }

  return (
    <Box>
      <Grid container item columnSpacing={2} xs={12} md={6}>
        <Grid item xs={12}>
          <h2 className="subtitle">Persoonsgegevens</h2>
        </Grid>
        <Grid item xs={6}>
          <LwFormInput
            name="firstName"
            label="Voornaam"
            control={control}
            rules={{ required: 'Voer een voornaam in' }}
          />
        </Grid>
        <Grid item xs={2}>
          <LwFormInput name="infix" label="Tussenvoegsel" control={control} />
        </Grid>
        <Grid item xs={4}>
          <LwFormInput
            name="lastName"
            label="Achternaam"
            control={control}
            rules={{ required: 'Voer een achternaam in' }}
          />
        </Grid>
        {shouldShowBsnField(flexWorkerData, allowRequiredVatForWorkActions) ? (
          <Grid item xs={6}>
            <LwFormInput
              name="bsn"
              label="BSN"
              control={control}
              inputType="number"
              rules={{
                minLength: { value: 9, message: 'BSN moet 9 cijfers bevatten' },
                maxLength: { value: 9, message: 'BSN moet 9 cijfers bevatten' },
              }}
            />
          </Grid>
        ) : null}
        <Grid item xs={6}>
          <LwFormInput
            name="dateOfBirth"
            label="Geboortedatum"
            helperText="dd-mm-jjjj"
            control={control}
            data-testid="dateOfBirth"
            rules={{
              validate: {
                validateDateOfBirth,
              },
              required: 'Voer een geboortedatum in',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <h2 className="subtitle">Zakelijke gegevens</h2>
        </Grid>
        <Grid item xs={6}>
          <LwFormDate
            name="smallBusinessSchemeStartDate"
            label="KOR Startdatum"
            control={control}
            rules={{
              validate: {
                validateBSDate,
                validateBSStartDateExists,
                validateBSStartEndDateComparison,
              },
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <LwFormDate
            name="smallBusinessSchemeEndDate"
            label="KOR Einddatum"
            rules={{
              validate: {
                validateBSDate,
                validateBSStartDateExists,
                validateBSStartEndDateComparison,
              },
            }}
            control={control}
          />
        </Grid>
        <Grid item xs={12}>
          <h2 className="subtitle">Level aanpassen</h2>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="center">
          <LwFormSelect
            sx={{ maxWidth: '200px' }}
            name="level"
            label="Kies level"
            icon={
              <LevelTooltip
                level={level ?? 'EXPLORER'}
                employmentType={flexWorkerData.employmentType}
                allowCandidateOnboarding={allowCandidateOnboarding}
              />
            }
            control={control}
            options={getAvailableLevelUpgrades(
              flexWorkerData.level,
              flexWorkerData.employmentType,
              allowCandidateOnboarding,
              isWorkerUnderAge
            )}
          />
          {flexWorkerData.employmentType === 'TEMPWORKER' &&
          isWorkerUnderAge &&
          workerAge != null ? (
            <WarningText pl={2}>
              Deze uitzendkracht is op dit moment {Math.floor(workerAge)} jaar: dit is onder de
              leeftijdgrens van {ageLimit} jaar. Het is daarom niet mogelijk om het level van deze
              uitzendkracht aan te passen.
            </WarningText>
          ) : null}
        </Grid>
        {allowEmploymentId ? (
          <>
            <Grid item xs={12}>
              <h2 className="subtitle">Employment ID</h2>
            </Grid>
            <Grid item xs={4}>
              <LwFormInput name="employmentId" label="Kies employment ID" control={control} />
            </Grid>
          </>
        ) : null}
      </Grid>
    </Box>
  );
};

const LevelTooltip = ({
  level,
  employmentType,
  allowCandidateOnboarding,
}: {
  level: Level;
  employmentType: WorkerEmploymentType;
  allowCandidateOnboarding: boolean;
}) => {
  return (
    <StyledTooltip
      title={
        <>
          <h3 className="subtitle">Flexwerker Levels</h3>
          <p>{getNextLevelRequirementsText(level, employmentType, allowCandidateOnboarding)}</p>
        </>
      }
    >
      <Box>
        <StyledQuestionIcon />
      </Box>
    </StyledTooltip>
  );
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} placement="right-start" classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.lwBlack[100],
    color: theme.palette.lwWhite[100],
    borderRadius: '10px',
  },
}));

const StyledQuestionIcon = styled(IconQuestion)(({ theme }) => ({
  fill: theme.palette.lwSecondary[40],
  marginLeft: theme.spacing(2),
}));

const WarningText = styled(Box)(({ theme }) => ({
  color: theme.palette.warning.main,
  paddingLeft: theme.spacing(2),
  fontSize: 13,
}));
